import baseApi from './RootBaseApi';
import {
  RunSettlementPayload,
  SettlementBlockedActorsListApiRaw,
  SettlementBlockedActorsListNormalized,
  SettlementBlockedActorsListParams,
  SettlementLinesListApiRaw,
  SettlementLinesListNormalized,
  SettlementLinesListParams,
  SettlementPreviewPeriodParams,
  SettlementPreviewPeriodResponse,
  SettlementRunApiItem,
  SettlementRunItem,
  SettlementRunsListApiRaw,
  SettlementRunsListNormalized,
  SettlementRunsListParams,
} from '@/types/settlements.types';

const mapRunApiItem = (item: SettlementRunApiItem): SettlementRunItem => ({
  ...item,
  total_net_payable: Number(item.total_net_payable ?? 0),
});

export const SettlementsService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSettlementRuns: builder.query<
      SettlementRunsListNormalized,
      SettlementRunsListParams
    >({
      query: (params) => ({
        url: 'settlements/admin',
        method: 'GET',
        params,
      }),
      transformResponse: (
        response: unknown,
        _meta,
        arg
      ): SettlementRunsListNormalized => {
        const raw = response as SettlementRunsListApiRaw;
        const items = Array.isArray(raw?.items)
          ? raw.items.map(mapRunApiItem)
          : [];

        return {
          items,
          totalItems: Number(raw?.total ?? items.length),
          currentPage: Number(raw?.page ?? arg?.page ?? 1),
          totalPages: Number(raw?.totalPages ?? 1),
          pageLimit: Number((raw?.limit ?? arg?.limit ?? items.length) || 10),
        };
      },
      providesTags: ['Settlements'],
      keepUnusedDataFor: 0,
    }),

    getSettlementPreviewPeriod: builder.query<
      SettlementPreviewPeriodResponse,
      SettlementPreviewPeriodParams
    >({
      query: (params) => ({
        url: 'settlements/admin/preview/period',
        method: 'GET',
        params,
      }),
      transformResponse: (response: unknown): SettlementPreviewPeriodResponse =>
        response as SettlementPreviewPeriodResponse,
      providesTags: ['Settlements'],
      keepUnusedDataFor: 0,
    }),

    getSettlementBlockedActors: builder.query<
      SettlementBlockedActorsListNormalized,
      SettlementBlockedActorsListParams
    >({
      query: ({ id, page, limit }) => ({
        url: `settlements/admin/${id}/blocked`,
        method: 'GET',
        params: {
          page,
          limit,
        },
      }),
      transformResponse: (
        response: unknown,
        _meta,
        arg
      ): SettlementBlockedActorsListNormalized => {
        const raw = response as SettlementBlockedActorsListApiRaw;
        const items = Array.isArray(raw?.items) ? raw.items : [];

        return {
          items,
          totalItems: Number(raw?.total ?? items.length),
          currentPage: Number(raw?.page ?? arg?.page ?? 1),
          totalPages: Number(raw?.totalPages ?? 1),
          pageLimit: Number((raw?.limit ?? arg?.limit ?? items.length) || 10),
        };
      },
      providesTags: ['Settlements'],
      keepUnusedDataFor: 0,
    }),

    getSettlementRunSummary: builder.query<SettlementRunItem, string>({
      query: (id) => ({
        url: `settlements/admin/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown): SettlementRunItem => {
        const raw = response as SettlementRunApiItem;
        return mapRunApiItem(raw);
      },
      providesTags: ['Settlements'],
      keepUnusedDataFor: 0,
    }),

    getSettlementLines: builder.query<
      SettlementLinesListNormalized,
      SettlementLinesListParams
    >({
      query: ({ id, page, limit }) => ({
        url: `settlements/admin/${id}/lines`,
        method: 'GET',
        params: {
          page,
          limit,
        },
      }),
      transformResponse: (
        response: unknown,
        _meta,
        arg
      ): SettlementLinesListNormalized => {
        const raw = response as SettlementLinesListApiRaw;
        const items = Array.isArray(raw?.items)
          ? raw.items.map((item) => ({
              ...item,
              gross_total: Number(item.gross_total ?? 0),
              vat_total: Number(item.vat_total ?? 0),
              commission_total: Number(item.commission_total ?? 0),
              net_total: Number(item.net_total ?? 0),
              carry_forward_amount: Number(item.carry_forward_amount ?? 0),
              payout_amount: Number(item.payout_amount ?? 0),
              psp_fee_total: Number(item.psp_fee_total ?? 0),
            }))
          : [];

        return {
          items,
          totalItems: Number(raw?.total ?? items.length),
          currentPage: Number(raw?.page ?? arg?.page ?? 1),
          totalPages: Number(raw?.totalPages ?? 1),
          pageLimit: Number((raw?.limit ?? arg?.limit ?? items.length) || 10),
        };
      },
      providesTags: ['Settlements'],
      keepUnusedDataFor: 0,
    }),

    runSettlement: builder.mutation<SettlementRunItem, RunSettlementPayload>({
      query: (payload) => ({
        url: 'settlements/admin/run',
        method: 'POST',
        data: payload,
      }),
      transformResponse: (response: unknown): SettlementRunItem => {
        const raw = response as SettlementRunApiItem;
        return mapRunApiItem(raw);
      },
      invalidatesTags: ['Settlements'],
    }),

    exportSettlementCsv: builder.query<Blob, string>({
      query: (id) => ({
        url: `settlements/admin/${id}/export`,
        method: 'GET',
        responseType: 'blob',
      }),
      transformResponse: (response: unknown): Blob => response as Blob,
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetSettlementRunsQuery,
  useGetSettlementRunSummaryQuery,
  useGetSettlementLinesQuery,
  useGetSettlementBlockedActorsQuery,
  useGetSettlementPreviewPeriodQuery,
  useRunSettlementMutation,
  useExportSettlementCsvQuery,
  useLazyExportSettlementCsvQuery,
} = SettlementsService;
